/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Loftsails Back in Stock
 * =======================
 * Subscribes shoppers to Klaviyo back-in-stock email notifications.
 *
 * Markup contract (see snippets/back-in-stock-form.liquid):
 *   [data-bis-form][data-bis-variant-id][data-bis-company]
 *     [data-bis-email]   <input type="email">
 *     [data-bis-submit]  <button>
 *     [data-bis-status]  <div aria-live="polite">
 *
 * The Klaviyo company_id (public "site ID") is read from data-bis-company
 * (theme setting), then auto-detected from the Klaviyo app onsite script.
 *
 * Version: 1.1.0
 */

(function () {
  'use strict';

  // Guard against double-init if the asset tag is rendered more than once.
  if (window.__lsBisInit) return;
  window.__lsBisInit = true;

  var CLIENT_REVISION = '2024-10-15';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function detectCompanyId(form) {
    var fromData = form.getAttribute('data-bis-company');
    if (fromData) return fromData.trim();

    // Fall back to the Klaviyo app's onsite script. Two known URL shapes:
    //   .../onsite/js/COMPANY_ID/klaviyo.js   (company_id as a path segment)
    //   ...?company_id=COMPANY_ID             (company_id as a query param)
    var scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite"]');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].src || '';
      var query = src.match(/[?&]company_id=([A-Za-z0-9]+)/);
      if (query) return query[1];
      var path = src.match(/\/onsite\/js\/([A-Za-z0-9]+)\//);
      if (path) return path[1];
    }
    return null;
  }

  function toKlaviyoVariantId(shopifyVariantId) {
    var id = String(shopifyVariantId || '').trim();
    if (!id) return '';
    if (id.indexOf('$shopify:::$default:::') === 0) return id;
    return '$shopify:::$default:::' + id;
  }

  function setStatus(form, message, tone) {
    var status = form.querySelector('[data-bis-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.remove('bis-form-2026__status--success', 'bis-form-2026__status--error');
    if (tone) status.classList.add('bis-form-2026__status--' + tone);
  }

  async function subscribe(form) {
    if (form.bisRequest || form.dataset.bisSubscribed === form.dataset.bisVariantId) return;
    var strings = JSON.parse(form.dataset.bisStrings);
    var emailInput = form.querySelector('[data-bis-email]');
    var submit = form.querySelector('[data-bis-submit]');
    var email = emailInput ? emailInput.value.trim() : '';
    var variantId = form.getAttribute('data-bis-variant-id');

    if (!EMAIL_RE.test(email)) {
      setStatus(form, strings.invalidEmail, 'error');
      if (emailInput) emailInput.focus();
      return;
    }

    var companyId = detectCompanyId(form);
    var klaviyoVariantId = toKlaviyoVariantId(variantId);
    if (!companyId || !klaviyoVariantId) {
      setStatus(form, strings.unavailable, 'error');
      console.error('LSBIS: missing', !companyId ? 'company_id' : 'variant id',
        '— set the Klaviyo Company ID theme setting or ensure the Klaviyo app is installed.');
      return;
    }

    var controller = new AbortController();
    form.bisRequest = controller;
    var isCurrent = function () {
      return form.bisRequest === controller && form.dataset.bisVariantId === variantId;
    };
    if (submit) submit.disabled = true;
    setStatus(form, strings.submitting, null);

    var subscribeUrl = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
      encodeURIComponent(companyId);

    try {
      var response = await fetch(subscribeUrl, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          revision: CLIENT_REVISION
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email }
                }
              }
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: klaviyoVariantId
                }
              }
            }
          }
        })
      });

      if (!isCurrent()) return;
      if (response.ok) {
        form.dataset.bisSubscribed = variantId;
        setStatus(form, strings.success, 'success');
        if (emailInput) emailInput.value = '';
        return;
      }

      // Keep Klaviyo's raw error (e.g. "variant ... does not exist") in the
      // console for debugging, but never surface it to the shopper.
      var data = await response.json().catch(function () { return {}; });
      if (!isCurrent()) return;
      var apiError = data.errors && data.errors[0] && (data.errors[0].detail || data.errors[0].title);
      if (apiError) console.error('LSBIS: subscribe rejected', response.status, apiError);
      setStatus(form, strings.unavailable, 'error');
      if (submit) submit.disabled = false;
    } catch (error) {
      if (!isCurrent() || error.name === 'AbortError') return;
      console.error('LSBIS: subscribe failed', error);
      setStatus(form, strings.networkError, 'error');
      if (submit) submit.disabled = false;
    } finally {
      if (isCurrent()) delete form.bisRequest;
    }
  }

  // Delegated handlers so forms shown/hidden after load (e.g. the variant
  // picker sold-out state) work without re-binding.
  document.addEventListener('click', function (event) {
    var submit = event.target.closest('[data-bis-submit]');
    if (!submit) return;
    var form = submit.closest('[data-bis-form]');
    if (!form) return;
    event.preventDefault();
    subscribe(form);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var input = event.target.closest('[data-bis-email]');
    if (!input) return;
    var form = input.closest('[data-bis-form]');
    if (!form) return;
    event.preventDefault();
    subscribe(form);
  });
})();
